import {
  BOARD_VARIANCE_COLUMNS,
  BOARD_VARIANCE_TABLE,
  REGISTER_ENTITY,
  REGISTER_MAX_PAGES,
  REGISTER_PAGE_SIZE,
  WORKBOOK_ENTITY,
  readPublishTime,
} from "./boardVariance.mjs";
import type { BoardVarianceRow } from "./boardVariance.d.mts";
import { ExecKpiFetchError, fetchAllPages, isTableMissingMessage } from "./execKpiFetch.mjs";
import { isColumnMissingError } from "./sanityGate";
import { getSupabase } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * MER-706: the staff variance register page's data access. Thin on purpose:
 * the paging loop is the Executive page's generic fetchAllPages (imported,
 * not changed), and every decision about what the rows mean lives in
 * boardVariance.mjs.
 *
 * Two kinds of read, both bound to the tenant through scopeTenant (MER-245;
 * a non-impersonating staff session matches every tenant under RLS, so the
 * application-layer binding is what keeps one customer's register from
 * blending into another's):
 *
 *   fetchBoardVarianceRows  The whole tenant register, paged past
 *                           PostgREST's 1,000-row cap in primary-key order so
 *                           no page overlaps. public.board_variance is
 *                           staff-read only (0058). Until 0058 is applied the
 *                           table does not exist: the error carries
 *                           tableMissing and the page says "Register not
 *                           available yet".
 *
 *   fetchPublishTimes       Three one-row reads of the publish log
 *                           (public.sync_log, staff-readable): the latest and
 *                           earliest successful EXEC_KPI publish and the
 *                           latest successful BOARD_VARIANCE publish. Each has
 *                           an abort timeout and resolves to null on any
 *                           failure, so the header can say "unknown" but can
 *                           never hang or show a database message.
 */

export { ExecKpiFetchError as BoardVarianceFetchError };

const PUBLISH_TIMEOUT_MS = 8000;

export interface BoardVarianceFetchResult {
  rows: BoardVarianceRow[];
  truncated: boolean;
  pages: number;
}

export interface PublishTimes {
  workbookLastPublished: string | null;
  workbookFirstPublished: string | null;
  registerLastPublished: string | null;
}

/** Both "table missing" classifiers, the execKpiData.ts precedent. */
function isMissing(message: string): boolean {
  return isTableMissingMessage(message) || isColumnMissingError(message);
}

/**
 * Every board_variance row for the tenant, in primary-key order. Throws the
 * shared fetch error; `tableMissing` is true when the table is not in the
 * schema yet.
 */
export async function fetchBoardVarianceRows(tenantId: string): Promise<BoardVarianceFetchResult> {
  const supabase = getSupabase();
  const result = await fetchAllPages<BoardVarianceRow>(
    (from, to) =>
      scopeTenant(supabase.from(BOARD_VARIANCE_TABLE).select(BOARD_VARIANCE_COLUMNS), tenantId)
        .order("check_kind", { ascending: true })
        .order("sheet", { ascending: true })
        .order("client_label", { ascending: true })
        .order("period", { ascending: true })
        .range(from, to)
        // The select list is a runtime string (pinned against 0058 by the
        // register test), so the typed column parser cannot read it.
        .overrideTypes<BoardVarianceRow[], { merge: false }>(),
    { pageSize: REGISTER_PAGE_SIZE, maxPages: REGISTER_MAX_PAGES, isMissing },
  );
  return { rows: result.rows, truncated: result.truncated, pages: result.pages };
}

function publishTime(tenantId: string, entityKey: string, newest: boolean): Promise<string | null> {
  return readPublishTime(
    () =>
      scopeTenant(getSupabase().from("sync_log").select("completed_at"), tenantId)
        .eq("entity_key", entityKey)
        .eq("status", "success")
        .not("completed_at", "is", null)
        .order("completed_at", { ascending: !newest })
        .limit(1)
        .abortSignal(AbortSignal.timeout(PUBLISH_TIMEOUT_MS)),
    {
      label: `${entityKey} ${newest ? "latest" : "earliest"} publish time`,
      warn: (message) => console.warn(message),
    },
  );
}

/** The publish-log times the register header shows. Never throws. */
export async function fetchPublishTimes(tenantId: string): Promise<PublishTimes> {
  const [workbookLastPublished, workbookFirstPublished, registerLastPublished] = await Promise.all([
    publishTime(tenantId, WORKBOOK_ENTITY, true),
    publishTime(tenantId, WORKBOOK_ENTITY, false),
    publishTime(tenantId, REGISTER_ENTITY, true),
  ]);
  return { workbookLastPublished, workbookFirstPublished, registerLastPublished };
}
